/**
 * Centralized media configuration — single source of truth.
 *
 * `next.config.ts` imports from here too, and that matters: Next rejects any
 * `quality` or width not allow-listed in `images`, so duplicating a number would
 * break rendering the moment the two drift.
 *
 * @see docs/internal/image-optimization-architecture.md
 */

// ─── Allowed remote hosts ────────────────────────────────────────────────────

/**
 * Hostnames whose images `next/image` is allowed to optimize.
 *
 * Single source of truth: `next.config.ts` derives `images.remotePatterns` from
 * this array, and `isOptimizableImageSrc` gates rendering on it. A URL served
 * from any other host is NOT optimizable — Next throws at render and takes the
 * whole page down — so components must treat unknown hosts as "no image" and
 * fall back to their placeholder rather than handing the URL to `next/image`.
 *
 * Keep this to real, production hosts only. Test/seed hosts (e.g.
 * `via.placeholder.com`) must never be added here; they degrade to the
 * fallback by design.
 */
export const ALLOWED_IMAGE_HOSTS = [
  // Brand CDN — list, section and Central photography.
  'static.taniabulhoes.com.br',
  // VTEX CDN — PDP product photography.
  'taniab.vtexassets.com',
  /*
   * Product media S3, **v1** — still what this VPS's backend serves.
   *
   * Kept alongside v2 below, not replaced by it: the two environments serve different buckets
   * right now (dev here on v1, staging on v2), and dropping either blanks every photograph in
   * that one.
   */
  'product-media-651373299804.s3.us-east-1.amazonaws.com',
  /*
   * Product media S3, **v2** — what staging serves since the backend's PR #54 (2026-10-06,
   * `infra/config/environments/stg.ts`, `PRODUCT_MEDIA_V2_BUCKET`).
   *
   * Same AWS account, new bucket, `tb-` prefix. Missing from this list, every catalog
   * photograph on staging simply disappeared: `isOptimizableImageSrc` rejects an unknown host
   * and each caller falls back to the porcelain monogram — silently, by design, because handing
   * the URL to `next/image` anyway throws and takes the whole page down. One line here covers
   * the PDP, the curation grid, Minha Lista and the guest's page, since all four ask the same
   * guard.
   */
  'tb-product-media-651373299804.s3.us-east-1.amazonaws.com',
  /*
   * Staging assets S3 — the banners of the three Listas Prontas, **and the hosts' own covers**.
   *
   * It is also where a host's uploaded list cover lives: the API presigns the upload against this
   * bucket (`S3_BUCKET`, `infra/config/environments/stg.ts`; this VPS's API uses it too), so the
   * `featuredImageUrl` of a list with its own photo is `https://tb-gift-list-stg-assets.s3.us-east-1…`.
   * The production bucket is not defined in the API repository yet — to be added here once it is.
   *
   * The banners part is **temporary**; the covers part is not, so removing this entry needs both
   * answered.
   *
   * The banners live on the brand CDN above, which is where production reads them. Staging's
   * backend refuses that host: its `TRUSTED_IMAGE_DOMAINS` is empty, so the only domain it trusts
   * for an image URL is this bucket, and `PATCH /gift-lists/:id` answers 400 for anything else
   * (measured 2026-10-06). So the same three photographs were copied here to let the templates
   * carry a banner in staging at all.
   *
   * **Remove this entry** once staging trusts `static.taniabulhoes.com.br`: the templates then
   * point at the brand CDN, which this list already allows, and nothing else depends on it.
   */
  'tb-gift-list-stg-assets.s3.us-east-1.amazonaws.com',
] as const;

// ─── Quality ─────────────────────────────────────────────────────────────────

/**
 * Quality presets for `next/image` (1–100), mirrored into `images.qualities`.
 *
 * Pick by role, not file size: when the photograph *is* the content it gets
 * `high`; when it supports copy that carries the message, `default`.
 */
export const IMAGE_QUALITY = {
  /**
   * Banners, hero, product detail, full-bleed editorial — usually the LCP.
   *
   * 85 and not 100: indistinguishable on photography once encoded to AVIF/WebP,
   * and 100 nearly doubled the payload (measured 45KB → 25KB at 640w).
   */
  high: 85,
  /** Cards, section illustrations, catalog grids — image supports the copy. */
  default: 80,
  /** Thumbnails, dense grids, small previews. */
  low: 70,
} as const;

// ─── Cache TTL (seconds) ─────────────────────────────────────────────────────

/**
 * Cache lifetimes per class of image.
 *
 * `minimumCacheTTL` takes `static`, the most conservative — Next applies one
 * global floor to the optimizer. The other two belong in `Cache-Control` at the
 * origin, where per-object headers are possible.
 */
export const IMAGE_CACHE_TTL = {
  /** Content-hash in the filename — effectively immutable (1 year). */
  immutable: 31_536_000,
  /** No hash, invalidated on redeploy: local branding and page photography (30 days). */
  static: 2_592_000,
  /** Product images, replaceable from the backoffice (1 day). */
  product: 86_400,
} as const;

// ─── Breakpoints (srcset generation) ─────────────────────────────────────────

/** Widths generated for responsive images (`fill` + `sizes`). */
export const DEVICE_SIZES = [640, 828, 1200, 1600, 1920] as const;

/** Widths generated for fixed-size images (explicit `width`/`height`). */
export const IMAGE_SIZES = [48, 96, 256, 384] as const;

// ─── Aspect Ratios (DS invariants) ───────────────────────────────────────────

/**
 * The product's standard aspect ratios, `product` being the DS invariant.
 *
 * Never interpolate into a class name — Tailwind only extracts literals, so call
 * sites write `aspect-[4/5]` and cite the key. Use directly in inline `style`.
 */
export const IMAGE_ASPECT_RATIOS = {
  /** Product photography (DS invariant). */
  product: '4 / 5',
  /** Hero full-bleed. */
  hero: '16 / 9',
  /** Square (editorial, avatar). */
  square: '1 / 1',
  /** Video embed on desktop. */
  videoDesktop: '16 / 7',
  /** Video embed on mobile. */
  videoMobile: '3 / 2',
  /** Editorial portrait (Central de Informações sidebar). */
  editorialPortrait: '5 / 6',
} as const;

// ─── Sizes presets (reusable `sizes` prop values) ────────────────────────────

/** Common `sizes` prop values for `<Image fill>`. */
export const IMAGE_SIZES_PRESETS = {
  /** Full-bleed (hero, duo CTA, newsletter). */
  fullBleed: '100vw',
  /** 3-column grid (≥1000px). */
  grid3: '(min-width: 1000px) 33vw, (min-width: 640px) 50vw, 80vw',
  /** 2-column grid. */
  grid2: '(min-width: 640px) 50vw, 100vw',
  /** Sidebar/aside image. */
  sidebar: '(min-width: 900px) 320px, 100vw',
  /**
   * A half-width photograph that sits beside its copy from 900px (the homepage's "Como montar" crop
   * for desktop, the duo's wide crops): full width below. The 640px step of `grid2` was wrong for
   * these, which stay stacked until 900px.
   */
  splitFromDesktop: '(min-width: 900px) 50vw, 100vw',
  /** The duo CTA's phone crops: stacked below 760px, side by side from there (its grid), so half width from 760px. */
  duoPhoneCrop: '(min-width: 760px) 50vw, 100vw',
  /** The catalog hero's cover: the right column of a two-column grid from 1024px (1.05fr / 0.95fr), the full width below. */
  listCover: '(min-width: 1024px) 48vw, 100vw',
} as const;
